// HeaderNavigation.jsx

import React, { useContext, useState, useEffect } from "react";
import {
  Header,
  Box,
  Image,
  Avatar,
  DropButton,
  CheckBox,
  Text,
  Button,
  Layer,
  // Heading,
} from "grommet";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../config/adminAxios";
import API_URL from "../../config/config";
import { AuthContext } from "../../contexts/AuthContext";
import LogoutButton from "../LogoutButton";
import UpgradeToManager from "./UpgradeToManager";
// import VideoTutorialsGrid from "../../components/ManagerPortal/VideoTutorialsGrid";
import UserTourModal from "../UserTourModal";

export const HeaderNavigation = ({ quizStarted = false, restrictedMode = false }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [userInitials, setUserInitials] = useState("");
  const [userid, setUserId] = useState("");
  const [jwtData, setJwtData] = useState("");

  // Track whether we are in manager view (toggle = "on")
  // Initialise from sessionStorage so the choice survives reloads/navigation.
  const [managerDashboardId, setManagerDashboardId] = useState(null);
  const [isManagerView, setIsManagerView] = useState(() => {
    const stored = sessionStorage.getItem("portalView");
    if (stored !== null) return stored === "manager";
    return /\/manager(\/|$)/i.test(location.pathname);
  });

  // Store a "pending" new toggle value while we ask for confirmation
  const [pendingView, setPendingView] = useState(null);

  // For showing/hiding the confirmation Layer
  const [showConfirm, setShowConfirm] = useState(false);

  // Control the DropButton’s open state (avoid using 'open' because it’s a restricted global)
  const [dropOpen, setDropOpen] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // const [actualUserId, setActualUserId] = useState(null);


  // useEffect(() => {
  //   // 1. grab token (adjust key if yours is different)
  //   if (!jwtToken) return;

  //   // 2. decode
  //   let decoded;
  //   try {
  //     decoded = jwtDecode(jwtToken);
  //   } catch {
  //     console.error('Invalid JWT');
  //     return;
  //   }

  //   // 3. pick correct ID based on role
  //   const { role, user_table_id, user_id } = decoded;
  //   const id =
  //     role === 'manager'
  //       ? user_table_id
  //       : role === 'user'
  //         ? user_id
  //         : null;

  //   setActualUserId(id);
  // }, [jwtToken]);

  const createTicketAndEmail = async (
    userId,
    type,
    subjectTemplate,
    bodyTemplate
  ) => {
    try {
      // 1. Create ticket
      const response = await fetch('/api/ticket/generate-ticket', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type })
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get('retry-after');
        const retryAt = retryAfter ? Date.now() + parseInt(retryAfter, 10) * 1000 : null;
        window.dispatchEvent(new CustomEvent('app:rate-limited', { detail: { retryAt } }));
        return;
      }

      if (!response.ok) {
        throw new Error(`Status ${response.status}`);
      }

      const { ticket_number } = await response.json();

      // 2. Build subject & body
      const subject = encodeURIComponent(
        `${subjectTemplate} [Ticket: ${ticket_number}]`
      );
      const body = encodeURIComponent(
        `${bodyTemplate}\n\nTicket Number: ${ticket_number}`
      );
      const bcc = encodeURIComponent('hpe.skilladvisor@meteoros.in');

      // 3. Open mailto
      const mailtoURL = `mailto:hpeskill.advisor@hpe.com` +
        `?subject=${subject}` +
        `&bcc=${bcc}` +
        `&body=${body}`;

      window.open(mailtoURL);
    } catch (err) {
      console.error('Unable to generate ticket', err);
      alert('Could not create ticket. Please try again.');
    }
  };


  // Persist toggle choice to sessionStorage whenever it changes.
  useEffect(() => {
    sessionStorage.setItem("portalView", isManagerView ? "manager" : "user");
  }, [isManagerView]);

  /**
   * 1) Sync to manager view when the URL explicitly contains "manager".
   *    We do NOT reset to user view on non-manager URLs — the user's explicit
   *    toggle choice (stored in sessionStorage) should survive navigation within
   *    the manager portal pages that don't have "manager" in their path.
   */
  useEffect(() => {
    if (/\/manager(\/|$)/i.test(location.pathname)) {
      setIsManagerView(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    setJwtData(user);
    const userId = user.user_table_id ?? user.user_id;
    setUserId(userId);

    const jwtIndicatesManager = user.role === "manager" || Boolean(user.user_table_id);
    setIsManager(jwtIndicatesManager);

    const fetchUserName = async () => {
      const response = await axios.get(`${API_URL}/user/getUserNameById/${userId}`);
      const name = response.data.name;
      setUserInitials(name.split(" ").map((n) => n[0]).join("").toUpperCase());
    };
    if (userId) fetchUserName();

    // JWT may be stale (e.g. after self-upgrade before re-login) — confirm from DB
    if (!jwtIndicatesManager && userId) {
      axios.get(`${API_URL}/user/is-manager/${userId}`)
        .then(({ data }) => {
          if (data.isManager) {
            setIsManager(true);
            setManagerDashboardId(data.managerId);
          } else {
            // Not a manager — clear stale portalView so user view is always shown
            setIsManagerView(false);
            sessionStorage.setItem("portalView", "user");
          }
        })
        .catch(() => {
          // On error, also reset to user view to be safe
          if (!jwtIndicatesManager) {
            setIsManagerView(false);
            sessionStorage.setItem("portalView", "user");
          }
        });
    } else if (!jwtIndicatesManager) {
      // No DB check needed AND not a manager — clear stale flag immediately
      setIsManagerView(false);
      sessionStorage.setItem("portalView", "user");
    }
  }, [user]);


  /**
   * 3) Toggle switch handler -> ask for confirmation
   */
  const handleToggleView = (event) => {
    const newChecked = event.target.checked;
    setPendingView(newChecked);
    setShowConfirm(true);
  };

  /**
   * 4) If user confirms, navigate to the new route
   */
  const handleConfirm = () => {
    if (pendingView == null) return; // no new value

    // Update sessionStorage synchronously before navigating to prevent stale
    // reads if this component unmounts before the isManagerView useEffect fires.
    sessionStorage.setItem("portalView", pendingView ? "manager" : "user");
    setIsManagerView(pendingView);
    setShowConfirm(false);

    if (pendingView) {
      // Manager view — use DB-resolved managerId for self-upgraded users whose JWT still says "user"
      const managerId = managerDashboardId ?? user?.user_id;
      navigate(`/${managerId}/manager/dashboard`);
    } else {
      // User view
      navigate(`/add-proficiency/${user?.user_table_id}`);
    }

    setPendingView(null);
  };

  /**
   * 5) If user cancels, revert the toggle
   */
  const handleCancel = () => {
    setShowConfirm(false);
    setPendingView(null);
  };

  /**
   * Determine which message to show in the confirmation dialog
   */
  const getConfirmationMessage = () => {
    if (pendingView === null) return "";
    return pendingView
      ? "You are switching to Manager View. Are you sure?"
      : "You are switching to User View. Are you sure?";
  };

  const handleUpgrade = () => {
    setIsManager(true); // Update manager status after successful upgrade
  };

  const dropdownContent = restrictedMode ? (
    <Box pad="small" align="center" justify="center">
      <LogoutButton />
    </Box>
  ) : (
    <Box pad="small" direction="column" gap="small">
      <Box align="center" justify="center">
        {/* Hide Home button when Manager portal is on */}
        {!isManagerView && (
          <Button
            label="Home"
            onClick={() =>
              navigate(
                // isManager
                //   ? `/${user?.user_id}/manager/dashboard`
                //   : `/add-proficiency/${userid}`
                `/add-proficiency/${userid}`,
              )
            }
          />
        )}
        <Button label="My profile" onClick={() => navigate(`/profile/${userid}`)} />
        <Button
          label="Dashboard"
          onClick={() =>
            navigate(
              isManagerView
                ? `/${managerDashboardId ?? user?.user_id}/manager/dashboard`
                : `/dashboard/user/${userid}`,
            )
          }
        />
        <Button
          label="Self guided help"
          onClick={() =>
            navigate(isManagerView ? `/manager/contact` : `/contact`)
          }
        />
        {!isManagerView && !isManager && (
          <UpgradeToManager user_id={userid} onUpgrade={handleUpgrade} />
        )}
      </Box>
      <Box
        border={{ color: "brand", size: "small" }}
      // margin={{ vertical: "small" }}
      />
      <Box align="center" justify="center">
        <LogoutButton />
      </Box>
    </Box>
  );

  return (
    <Header
      fill="horizontal"
      background="black"
      pad={{ vertical: "small", horizontal: "large" }}
      height="70px"
      style={{ position: "relative" }} // Ensure relative positioning for absolute elements
    >
      {/* LEFT: Logo */}
      <Box
        direction="row"
        align="center"
        gap="small"
      // onClick={() => navigate(`/dashboard/${userid}`)}
      >
        <a
          href="https://education.hpe.com/ww/en/training/index.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Image
            src="/hpe-logo.png"
            alt="App Logo"
            width="130px"
            height="55px"
          />
        </a>
      </Box>

      {/* ABSOLUTE POSITIONED RIGHT BOX */}
      {!quizStarted && ( // Only render if quizStarted is false
        <>
          {!restrictedMode && user && (user.role === "manager" || Boolean(user.user_table_id)) && (
            <Box
              direction="row"
              align="center"
              gap="xsmall"
              justify="center"
              flex
              alignSelf="center"
              alignContent="center"
              margin={{ right: "130px" }}
            >
              <Text
                weight={!isManagerView ? "bold" : undefined}
                color={!isManagerView ? "accent-1" : "light-1"}
              >
                User portal
              </Text>

              <Box basis="60px" align="center">
                <CheckBox
                  toggle
                  checked={isManagerView}
                  onChange={handleToggleView}
                  align="center"
                />
              </Box>

              <Text
                weight={isManagerView ? "bold" : undefined}
                color={isManagerView ? "accent-1" : "light-1"}
              >
                Manager portal
              </Text>
            </Box>
          )}

          <Box
            align="end"
            direction="row"
            gap="small"
            style={{
              position: "absolute",
              right: "40px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {/* {isManagerView && (
                  <Button
                    label="Video Tutorials"
                    onClick={() => setShowVideoModal(true)}
                    alignSelf="center"
                  />
                )} */}

            {/* Avatar + DropButton */}
            {user && (
              <Box direction="row" width="auto" gap="small" align="center">
                {!restrictedMode && !isManagerView && !quizStarted && (
                  <Text
                    onClick={() => setShowModal(true)}
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    Quick start Guide
                  </Text>
                )}
                <DropButton
                  dropAlign={{ top: "bottom", right: "right" }}
                  dropContent={dropdownContent}
                  open={dropOpen}
                  onOpen={() => setDropOpen(true)}
                  onClose={() => setDropOpen(false)}
                >
                  <Avatar background="brand" size="medium">
                    {userInitials || "?"}
                  </Avatar>
                </DropButton>
              </Box>
            )}
          </Box>
          {showModal && (
            <UserTourModal
              showModal={showModal}
              setShowModal={setShowModal}
              userid={userid}
            />
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <Layer
          onEsc={handleCancel}
          onClickOutside={handleCancel}
          responsive={false}
        >
          <Box pad="medium" gap="small" width="medium">
            <Text>{getConfirmationMessage()}</Text>
            <Box direction="row" gap="medium" justify="center">
              <Button label="Confirm" primary onClick={handleConfirm} />
              <Button label="Cancel" onClick={handleCancel} />
            </Box>
          </Box>
        </Layer>
      )}

      {/* {showVideoModal && (
          <Layer
            onEsc={() => setShowVideoModal(false)}
            onClickOutside={() => setShowVideoModal(false)}
            margin="small"
            background="white"
          >
            <Box pad="medium" width="large" round="small">
              <Heading level={3} margin="none" textAlign="center" alignSelf="center" align="center" style={{color: "black"}}> 
                Watch Our Tutorials
              </Heading>
              <Text margin={{ bottom: "small" }} textAlign="center">
                Explore our video tutorials to get the most out of Skill Advisor.
              </Text>
              <Box width="xlarge" pad="medium" background="light-1" round="small">
                <Box align="center">
                  <VideoTutorialsGrid />
                </Box>
              </Box>
              <Box align="center" margin={{ top: "medium" }}>
                <Button label="Close" style={{color: "black"}} onClick={() => setShowVideoModal(false)} />
              </Box>
            </Box>
          </Layer>
        )} */}
    </Header>
  );
};
